import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, map, Observable } from "rxjs";
import { DemoUserViewModel, InternalGroupViewModel, LoggedUserViewModel, TeamDto } from "../core";
import { AccountInfo } from "@azure/msal-browser";
import { roles } from "../app.config";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root"
})
export class UserService {
  private readonly controllerName = "User";
  private apiUrl: string = `${environment.API}/${this.controllerName}`;
  private roles?: string[];

  private rolesSubject = new BehaviorSubject<string[] | undefined>(undefined);
  roles$ = this.rolesSubject.asObservable();

  userInfoSubject = new BehaviorSubject<LoggedUserViewModel | undefined>(undefined);
  userInfo$ = this.userInfoSubject.asObservable();

  private teamSubject = new BehaviorSubject<TeamDto>({} as TeamDto);
  team$ = this.teamSubject.asObservable();

  private internalGroupsSubject = new BehaviorSubject<InternalGroupViewModel[]>([]);
  internalGroups$ = this.internalGroupsSubject.asObservable();

  constructor(private http: HttpClient) {
    this.roles$.subscribe(r => {
      this.roles = r;
    });
  }

  public setLoggedUser(accountInfo?: AccountInfo): void {
    if (accountInfo) {
      this.userInfoSubject.next({
        username: accountInfo.idTokenClaims["email"] as string,
        fullName: accountInfo.name ?? "",
        isBoardUser: !!accountInfo.idTokenClaims["isBoardUser"]
      });
      this.registerUser();
    }
    this.rolesSubject.next(accountInfo?.idTokenClaims?.roles);
  }

  public isSuperUser(): boolean {
    return (this.roles?.findIndex(x => x === roles.SuperUser) ?? -1) > -1;
  }

  public isBoardUser(): boolean {
    return this.userInfoSubject.value?.isBoardUser;
  }
  public registerUser(): void {
    this.http.get(`${this.apiUrl}/registerUser`).subscribe();
  }

  public getTeamMembers(): Observable<TeamDto> {
    return this.http.get<TeamDto>(`${this.apiUrl}/team`).pipe(
      map(res => {
        this.teamSubject.next(res);
        return res;
      })
    );
  }

  public removeTeamMember(userName: string): Observable<DemoUserViewModel> {
    return this.http.delete<DemoUserViewModel>(`${this.apiUrl}/team/${userName}`).pipe(
      map(res => {
        const { teamName, users } = this.teamSubject.value;
        const newTeam = [...users.filter(x => x.userName !== res.userName)];
        this.teamSubject.next({
          teamName: teamName,
          users: newTeam
        });

        return res;
      })
    );
  }

  public getInternalGroups(): Observable<InternalGroupViewModel[]> {
    return this.http.get<InternalGroupViewModel[]>(`${this.apiUrl}/groups`).pipe(
      map(res => {
        this.internalGroupsSubject.next(res);
        return res;
      })
    );
  }
}
